import { Button, Group, Modal, ScrollArea, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core';
import { useEffect, useState } from 'react';
import { primaryColor } from '../../constants/theme';
import { useApp } from '../../context/AppContext';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];

const pad = (n: number) => String(n).padStart(2, '0');

interface TimePickerModalProps {
  opened: boolean;
  label: string;
  initialTime: string;
  field: 'start' | 'end';
  otherTime: string;
  onClose: () => void;
  onConfirm: (time: string) => void;
}

export const TimePickerModal = ({
  opened,
  label,
  initialTime,
  field,
  otherTime,
  onClose,
  onConfirm,
}: TimePickerModalProps) => {
  const [hour, setHour] = useState(() => {
    const [h] = initialTime.split(':');
    return parseInt(h, 10);
  });
  const [minute, setMinute] = useState(() => {
    const [, m] = initialTime.split(':');
    return parseInt(m, 10);
  });

  useEffect(() => {
    if (opened) {
      const [h, m] = initialTime.split(':');
      setHour(parseInt(h, 10));
      setMinute(parseInt(m, 10));
    }
  }, [opened]);

  const { isTabletAndBelow } = useApp();

  const selectedMinutes = hour * 60 + minute;
  const otherMinutes = otherTime.split(':').reduce((h, m, i) => i === 0 ? parseInt(m) * 60 : h + parseInt(m), 0);
  const isSelectionValid = field === 'start' ? selectedMinutes < otherMinutes : selectedMinutes > otherMinutes;
  const doneLabel = isSelectionValid
    ? 'Done'
    : field === 'start'
    ? 'Start time must be before end time'
    : 'End time must be after start time';

  const handleConfirm = () => {
    onConfirm(`${pad(hour)}:${pad(minute)}`);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      padding="lg"
      title={
        <Text fw={600} size="h4">
          {label}{' '}
          <Text component="span" c={primaryColor} fw={600} size="h4">
            {pad(hour)}:{pad(minute)}
          </Text>
        </Text>
      }
    >
      <Stack gap="md">

        <Stack gap={6}>
          <Text size="sm" fw={600} ta="left">
            Hour
          </Text>
          {isTabletAndBelow ? (
            <ScrollArea type="auto" offsetScrollbars>
              <Group wrap="nowrap" gap={6} pb={4}>
                {HOURS.map((h) => (
                  <UnstyledButton
                    key={h}
                    onClick={() => setHour(h)}
                    style={(theme) => ({
                      border: `1px solid ${hour === h ? theme.colors[primaryColor]?.[6] ?? primaryColor : theme.colors.gray[4]}`,
                      borderRadius: theme.radius.sm,
                      padding: '6px 8px',
                      textAlign: 'center',
                      background: hour === h ? (theme.colors[primaryColor]?.[6] ?? primaryColor) : 'transparent',
                      color: hour === h ? '#fff' : 'inherit',
                      fontWeight: hour === h ? 600 : 400,
                      fontSize: theme.fontSizes.sm,
                      minWidth: 40,
                      flexShrink: 0,
                    })}
                  >
                    {pad(h)}
                  </UnstyledButton>
                ))}
              </Group>
            </ScrollArea>
          ) : (
            <SimpleGrid cols={6} spacing={6}>
              {HOURS.map((h) => (
                <UnstyledButton
                  key={h}
                  onClick={() => setHour(h)}
                  style={(theme) => ({
                    border: `1px solid ${hour === h ? theme.colors[primaryColor]?.[6] ?? primaryColor : theme.colors.gray[4]}`,
                    borderRadius: theme.radius.sm,
                    padding: '6px 4px',
                    textAlign: 'center',
                    background: hour === h ? (theme.colors[primaryColor]?.[6] ?? primaryColor) : 'transparent',
                    color: hour === h ? '#fff' : 'inherit',
                    fontWeight: hour === h ? 600 : 400,
                    fontSize: theme.fontSizes.sm,
                  })}
                >
                  {pad(h)}
                </UnstyledButton>
              ))}
            </SimpleGrid>
          )}
        </Stack>

        <Stack gap={6}>
          <Text size="sm" fw={600} ta="left">
            Minute
          </Text>
          <Group gap={8}>
            {MINUTES.map((m) => (
              <UnstyledButton
                key={m}
                onClick={() => setMinute(m)}
                style={(theme) => ({
                  border: `1px solid ${minute === m ? theme.colors[primaryColor]?.[6] ?? primaryColor : theme.colors.gray[4]}`,
                  borderRadius: theme.radius.sm,
                  padding: '8px 16px',
                  textAlign: 'center',
                  background: minute === m ? (theme.colors[primaryColor]?.[6] ?? primaryColor) : 'transparent',
                  color: minute === m ? '#fff' : 'inherit',
                  fontWeight: minute === m ? 600 : 400,
                  fontSize: theme.fontSizes.sm,
                  flex: 1,
                })}
              >
                :{pad(m)}
              </UnstyledButton>
            ))}
          </Group>
        </Stack>

        <Button color={primaryColor} fullWidth disabled={!isSelectionValid} onClick={handleConfirm}>
          {doneLabel}
        </Button>
      </Stack>
    </Modal>
  );
};
